import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import classNames from 'classnames';

import { OnboardingStyle } from '../../../public/onboarding/onboarding-style';
import { hideModal, showModal, showToast } from '../../../../store/slices/generalSlice';
import { RootState } from '../../../../store/rootReducer';
import storageService from '../../../../services/storage.service';
import { queryClient } from '../../../../queryClient';
import useManageStorage from '../../../../hooks/useManageStorage/useManageStorage';
import useAssistance from '../../../../hooks/useAssistance/useAssistance';
import { AuthHeader, FormLayout } from '../../../../global-styles/layout';
import { Heading2Text, Subhead02Text } from '../../../../components/typography/typography';
import Navbar from '../../../../components/navbar/navbar';
import ModalContainer from '../../../../components/common/modal-container/modal-container';
import AlxButton from '../../../../components/alx-components/button';
import { images } from '../../../../assets/images/images';
import { IInOurWayProps } from './success-types';
import { InOurWayStyle } from './in-our-way-style';

const InOurWay: React.FC<IInOurWayProps> = ({ assistanceInfo, nextStep, resetForms, isDelayed }) => {
  const { t } = useTranslation(['common', 'auth', 'assistance']);
  const dispatch = useDispatch();
  const { writeToStorage } = useManageStorage();

  const {
    cancelAssistance,
    requestAssistanceInformation: { data: assistanceData },
  } = useAssistance({
    ...(!assistanceInfo?.intervention
      ? {
          assistanceInformationParams: {
            identifier: assistanceInfo?.identifier,
            insuranceCompany: assistanceInfo?.insuranceCompany,
          },
        }
      : {}),
  });

  const navigate = useNavigate();
  const { deleteFromStorage } = useManageStorage();

  const modal = useSelector((state: RootState) => state.general.modal);

  let right = {};

  if (assistanceInfo) {
    right = {
      clickRight: () => {
        dispatch(showModal({ name: 'cancel-assitance' }));
      },
      customRight: (
        <div className="onboarding-nav">
          <p>{t('auth:unregistered.success.cancel-service')}</p>
        </div>
      ),
    };
  }

  useEffect(() => {
    const handleAssistanceData = async () => {
      if (assistanceData?.intervencion) {
        const bodyToStorage = {
          provider: assistanceData.colaborador,
          file: assistanceData.expediente,
          intervention: assistanceData.intervencion,
          identifier: assistanceInfo?.identifier,
          providerName: assistanceData.colaborador,
        };
        await writeToStorage('assitanceInformation', bodyToStorage);
        queryClient.setQueryData(['assitanceInformation'], bodyToStorage);
      }
    };
    handleAssistanceData();
  }, [assistanceData, assistanceInfo?.identifier, writeToStorage]);

  const generateTitle = () => {
    if (isDelayed) {
      return t('assistance:aia.assistance-in-our-way.title-delayed');
    }
    return assistanceInfo?.intervention || assistanceData?.intervencion
      ? t('assistance:aia.assistance-in-our-way.title-on-the-way')
      : t('assistance:aia.assistance-in-our-way.title');
  };

  return (
    <>
      <InOurWayStyle>
        <OnboardingStyle>
          <Navbar assistance {...right} />
          <div className="onboarding-top">
            <AuthHeader>
              <div className="auth-header-title ">
                <Heading2Text as="h1" weight={600}>
                  {generateTitle()}
                </Heading2Text>
              </div>
            </AuthHeader>
            <div className="success-img">
              <img src={images.cars2} alt="" />
            </div>
            <div className="success-text in-our-way-text m-24px">
              <Subhead02Text> {t('assistance:aia.assistance-in-our-way.text')}</Subhead02Text>
              <Subhead02Text> {t('assistance:aia.assistance-in-our-way.text2')}</Subhead02Text>
            </div>
          </div>
          <FormLayout>
            <AlxButton
              className={classNames('success-next-btn', {
                'Mui-disabled': !(assistanceInfo?.intervention || assistanceData?.intervencion),
              })}
              onClick={() => {
                if (assistanceInfo?.intervention || assistanceData?.intervencion) {
                  if (nextStep) {
                    nextStep();
                  }
                } else {
                  dispatch(showToast({ message: t('assistance:aa.pending-crane'), severity: 'error', open: true }));
                }
              }}
              data-cy="login-submit"
            >
              {t('common:buttons.next')}
            </AlxButton>
          </FormLayout>
        </OnboardingStyle>
      </InOurWayStyle>
      {/* MODALS */}
      {assistanceInfo && (
        <ModalContainer
          buttons={[
            {
              className: 'primary',
              onClick: () => {
                dispatch(hideModal());
                cancelAssistance.mutate(assistanceInfo, {
                  onSuccess: async () => {
                    await storageService.removeItem('metaData');
                    deleteFromStorage(['interventionData', 'activeAssistance', 'assitanceInformation']);
                    navigate('/cancelled');
                  },
                  onError: (err) => {
                    console.error(err, 'something went wrong');
                  },
                });
              },
              label: t('auth:unregistered.success.cancel-assistance-modal.cancel-assistance-button'),
            },
            {
              className: 'secondary',
              onClick: () => dispatch(hideModal()),
              label: t('auth:unregistered.success.cancel-assistance-modal.cancel-button'),
            },
          ]}
          active={modal.name === 'cancel-assitance'}
          onClose={() => {
            dispatch(hideModal());
          }}
          title={t('auth:unregistered.success.cancel-assistance-modal.title')}
          text={t('auth:unregistered.success.cancel-assistance-modal.description')}
          dataCy="reset-form-modal"
        />
      )}

      <ModalContainer
        buttons={[
          {
            className: 'primary',
            onClick: () => {
              if (resetForms) resetForms();
              navigate('/');
            },
            label: t('common:buttons.accept'),
          },
        ]}
        active={modal.name === 'assitance-cancel-successful'}
        onClose={() => {
          dispatch(hideModal());
          if (resetForms) resetForms();
          navigate('/');
        }}
        title={t('auth:unregistered.success.confirmed-assistance-cancelation.title')}
        text={t('auth:unregistered.success.confirmed-assistance-cancelation.description')}
        dataCy="reset-form-modal"
      />
    </>
  );
};

export default InOurWay;
